// File System Access API helpers for saving SMRT COP photos straight into a
// user-chosen folder on disk. The Ericsson upload portal blocks clipboard
// paste, so instead of copying an image the crew downloads it and drags the
// file in from their own folder. Chromium only (Chrome/Edge) — callers guard
// on isFsAccessSupported(). Types aren't in every TS lib yet, so we cast.

import { sectorTokenForKey } from "./submissionPhotos";

type DirHandle = any; // FileSystemDirectoryHandle

export const isFsAccessSupported = (): boolean =>
  typeof (window as any).showDirectoryPicker === "function";

// Prompt the user to pick/create a destination folder. Rejects (throws
// AbortError) if they cancel the picker — callers treat that as "abort".
export async function pickPhotoDirectory(): Promise<DirHandle> {
  return await (window as any).showDirectoryPicker({ mode: "readwrite" });
}

// Ensure we still hold read-write permission on a previously-picked folder
// (Chrome can drop it). Re-requests it (which may show a one-click prompt);
// returns false if the user denies — callers then re-pick the folder.
export async function ensureDirPermission(handle: DirHandle): Promise<boolean> {
  try {
    const opts = { mode: "readwrite" as const };
    if ((await handle.queryPermission?.(opts)) === "granted") return true;
    if ((await handle.requestPermission?.(opts)) === "granted") return true;
    return false;
  } catch {
    return false;
  }
}

// Strip characters Windows forbids in filenames so a write never fails; keeps
// the name otherwise intact (spaces, dots, dashes are all valid).
const safeFileName = (name: string): string =>
  (name || "photo").replace(/[\\/:*?"<>|]/g, "_").trim() || "photo";

// The name a photo is SAVED AS on disk: its stored display name, prefixed with
// the sector token from its key ("A_Azimuth_1_ARL02613.jpg").
//
// Necessary because a v2 checklist builds the display name from the category
// label alone, so Alpha/Beta/Gamma all produce the SAME name — and since
// writeFileToDir overwrites, a 3-sector site silently saved one sector's worth
// of files instead of three. The prefix also groups the folder by sector.
//
// Legacy sites whose labels already carry the sector are left alone: the token
// only comes from the key, and a key with no sector yields no prefix.
export function downloadFileName(key: string, storedName: string): string {
  const name = storedName || "photo";
  const sector = sectorTokenForKey(key);
  if (!sector) return name;
  // Don't double up if the name already starts with that token.
  if (new RegExp(`^${sector}[_ -]`, "i").test(name)) return name;
  return `${sector}_${name}`;
}

// Write bytes into the chosen folder under `fileName`. A same-named file is
// OVERWRITTEN silently, so callers must pass a name that is unique per photo —
// use downloadFileName(), not the raw stored name.
export async function writeFileToDir(
  dir: DirHandle,
  fileName: string,
  bytes: Uint8Array,
): Promise<void> {
  const fileHandle = await dir.getFileHandle(safeFileName(fileName), {
    create: true,
  });
  const writable = await fileHandle.createWritable();
  await writable.write(bytes);
  await writable.close();
}

// Fetch a stored photo's raw bytes via the backend base64 proxy (direct browser
// SAS is disabled by the storage firewall) — same source the copy/open paths use.
export async function fetchPhotoBytes(
  backendUrl: string,
  siteId: string,
  fieldKey: string,
  blobName: string,
): Promise<Uint8Array> {
  const r = await fetch(
    `${backendUrl}/smrtcop/files/${encodeURIComponent(siteId)}/${encodeURIComponent(fieldKey)}?blobName=${encodeURIComponent(blobName)}&format=base64`,
    { credentials: "include" },
  );
  if (!r.ok) throw new Error(`download ${r.status}`);
  const data = await r.json();
  const binary = atob(data.base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}
